import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../context/AuthContext";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { FILM_LENGTHS, FILM_GENRES } from "../constants/film";

const empty = {
  title: "",
  director: "",
  description: "",
  youtube_id: "",
  year: new Date().getFullYear(),
  duration: "",
  length: "Short",
  genres: [],
  tags: "",
  submitter_name: "",
  submitter_email: "",
};

export default function SubmitFilm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await axios.post(`${API}/submissions/films`, payload);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Please check the fields and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleGenre = (g) => set("genres", form.genres.includes(g) ? form.genres.filter((x) => x !== g) : [...form.genres, g]);

  if (done) {
    return (
      <div className="bg-[#FFF8E7] min-h-[70vh]" data-testid="page-submit-film-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="bg-[#FFD93D] border-2 border-[#0A0A0A] pop-shadow p-10">
            <CheckCircle2 className="w-12 h-12 text-[#0A0A0A] mb-4" />
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-3">Thank you</div>
            <h1 className="font-heading font-black text-[#0A0A0A] text-3xl sm:text-4xl tracking-tight leading-tight">
              Submission received.
            </h1>
            <p className="mt-4 text-[#0A0A0A] leading-relaxed">
              Your film is now pending review by the Divergent Cinema team. We&apos;ll be in touch at the email you provided.
            </p>
            <button
              onClick={() => { setDone(false); setForm(empty); }}
              data-testid="submit-another-btn"
              className="mt-6 bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            >
              Submit another <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/films")}
              className="mt-6 ml-3 bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-6 py-3 text-sm font-medium"
            >
              Back to Films
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-submit-film">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Open submissions</div>
        <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl tracking-tight leading-[0.95]">Submit a film.</h1>
        <p className="mt-4 text-[#2C2C2C] leading-relaxed">
          Are you a neurodivergent filmmaker? Submit your YouTube link below. Our team reviews submissions and approves them onto the public Films page.
        </p>

        <form onSubmit={submit} className="mt-10 bg-white border-2 border-[#0A0A0A] pop-shadow p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="submit-film-form">
          <Field label="Film title" required>
            <input data-testid="field-title" value={form.title} onChange={(e) => set("title", e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Director" required>
            <input data-testid="field-director" value={form.director} onChange={(e) => set("director", e.target.value)} required className={inputCls} />
          </Field>
          <Field label="YouTube URL or video ID" required full hint="Paste the YouTube link, e.g. https://youtu.be/xxxxxxxxxxx">
            <input data-testid="field-youtube" value={form.youtube_id} onChange={(e) => set("youtube_id", e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Year" required>
            <input data-testid="field-year" type="number" min="1900" max="2100" value={form.year} onChange={(e) => set("year", e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Duration" required hint="e.g. 12:30">
            <input data-testid="field-duration" value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="MM:SS" required className={inputCls} />
          </Field>
          <Field label="Length" required full hint="Pick one — describes the format, not the genre">
            <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="field-length">
              {FILM_LENGTHS.map((l) => {
                const active = form.length === l;
                return (
                  <button
                    key={l}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => set("length", l)}
                    data-testid={`length-${l.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    className={`px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] transition-colors ${active ? "bg-[#FF2E63] text-[#FFF8E7]" : "bg-white text-[#0A0A0A] hover:bg-[#FFD93D]"}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Genres" full hint="Tick all that apply — a documentary can also be a comedy, for example">
            <div className="flex flex-wrap gap-2" role="group" data-testid="field-genres">
              {FILM_GENRES.map((g) => {
                const active = form.genres.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleGenre(g)}
                    data-testid={`genre-${g.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] transition-colors ${active ? "bg-[#1A5DFF] text-[#FFF8E7]" : "bg-white text-[#0A0A0A] hover:bg-[#FFD93D]"}`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Tags" full hint="Optional, comma-separated — e.g. autism, coming-of-age, sound">
            <input data-testid="field-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Description" required full>
            <textarea data-testid="field-description" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Your name" required>
            <input data-testid="field-submitter-name" value={form.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Your email" required>
            <input data-testid="field-submitter-email" type="email" value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} required className={inputCls} />
          </Field>

          {error && (
            <div className="md:col-span-2 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm" data-testid="submit-film-error" role="alert">
              {error}
            </div>
          )}

          <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 items-start">
            <button type="submit" disabled={submitting} data-testid="submit-film-btn" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-8 py-4 text-base font-medium inline-flex items-center gap-2">
              {submitting ? "Submitting…" : "Submit film"} <ArrowRight className="w-4 h-4" />
            </button>
            <div className="text-xs text-[#2C2C2C] sm:max-w-sm pt-2">By submitting, you confirm you own or have rights to share this film.</div>
          </div>
        </form>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none";

function Field({ label, hint, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A]">
        {label} {required && <span className="text-[#FF2E63]">*</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-[#2C2C2C]">{hint}</span>}
    </label>
  );
}
